import React, { useEffect, useState, useMemo, useCallback } from 'react';
import RcTooltip from 'rc-tooltip';
import sanitizeHtml from 'sanitize-html';
import Tooltip from '@src/components/Tooltip';
import { useTranslation } from 'react-i18next';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import CommonButton from '@src/new-components/Button';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import { DEFAULT_RETENTION_DAYS } from '@src/constants/voice';
import Typography from '@src/new-components/Typography';
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded';
import { IconButton } from '@mui/material';
import { COLOR } from '@src/styles/color';
import {
  StyledAdvancedFilter,
  StyledListTagsFilter,
  StyledTagFilter,
} from './index.style';

const AdvancedFilterButton = ({
  handleChangeFilter,
  dataFilter,
  options,
  isVbee,
  isMyVoice,
}) => {
  const { t } = useTranslation();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState({});

  useEffect(() => {
    setSelectedFilter({
      ...dataFilter.advancedFilter,
      ...(!isVbee &&
        !isMyVoice && {
          retentionDays: dataFilter?.advancedFilter?.retentionDays?.length
            ? dataFilter.advancedFilter.retentionDays
            : [DEFAULT_RETENTION_DAYS],
        }),
    });
  }, [isTooltipOpen, dataFilter.advancedFilter]);

  const hasFilter = useMemo(
    () =>
      Object.values(dataFilter?.advancedFilter || {})
        ?.filter((value) => value?.[0] !== DEFAULT_RETENTION_DAYS)
        ?.some((value) => Array.isArray(value) && value.length > 0),
    [dataFilter],
  );

  const handleTooltipVisibleChange = useCallback((visible) => {
    setIsTooltipOpen(visible);
  }, []);

  const handleSelectFilter = useCallback((key, value) => {
    setSelectedFilter((prev) => {
      if (key === 'retentionDays') {
        return { ...prev, [key]: [value] };
      }
      const prevArr = Array.isArray(prev[key]) ? prev[key] : [];
      const exists = prevArr.includes(value);
      return {
        ...prev,
        [key]: exists
          ? prevArr.filter((v) => v !== value)
          : [...prevArr, value],
      };
    });
  }, []);

  const handleApplyFilter = useCallback(() => {
    const isFilterInit =
      !selectedFilter.gender?.length &&
      !selectedFilter.ageGroup?.length &&
      selectedFilter.retentionDays[0] === DEFAULT_RETENTION_DAYS &&
      !selectedFilter.level?.length &&
      !selectedFilter.voiceCloningType?.length;

    handleChangeFilter(
      'advancedFilter',
      isFilterInit
        ? {
            ageGroup: [],
            gender: [],
            retentionDays: [],
            level: [],
            voiceCloningType: [],
          }
        : selectedFilter,
    );
    setIsTooltipOpen(false);
  }, [handleChangeFilter, selectedFilter]);

  const handleResetFilter = useCallback(() => {
    setSelectedFilter({
      retentionDays: isMyVoice || isVbee ? [] : [DEFAULT_RETENTION_DAYS],
      ageGroup: [],
      gender: [],
      level: [],
    });
  }, [isMyVoice, isVbee]);

  const renderFilterTags = (key) =>
    options[key]?.map((item) => (
      <StyledTagFilter
        key={item.value}
        onClick={() => handleSelectFilter(key, item.value)}
        className={`${
          selectedFilter[key]?.includes(item.value) ? 'active' : ''
        }`}
      >
        <Typography
          variant="body-14-medium"
          color={
            selectedFilter[key]?.includes(item.value)
              ? COLOR.descriptionGray
              : COLOR.descriptionUpload
          }
        >
          {item.label}
        </Typography>
      </StyledTagFilter>
    ));

  return (
    <>
      {isTooltipOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'transparent',
            zIndex: 9999,
            pointerEvents: 'auto',
          }}
          onWheel={(e) => e.preventDefault()}
          onTouchMove={(e) => e.preventDefault()}
        />
      )}
      <RcTooltip
        placement="bottomRight"
        overlay={
          <StyledAdvancedFilter>
            {Object.keys(options).map((key) => (
              <div
                key={key}
                style={{
                  marginBottom: '24px',
                  gap: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <Typography variant="body-14-medium" color={COLOR.darkGray}>
                    {t(key)}
                  </Typography>
                  {key === 'retentionDays' && (
                    <Tooltip
                      placement="top"
                      arrow
                      title={
                        <div
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(t('tooltipRetentionDays')),
                          }}
                        />
                      }
                    >
                      <InfoRoundedIcon
                        sx={{ fontSize: '16px', color: COLOR.indigoIcon }}
                      />
                    </Tooltip>
                  )}
                </div>
                <StyledListTagsFilter>
                  {renderFilterTags(key)}
                </StyledListTagsFilter>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
              }}
            >
              <CommonButton
                variant="outlineSmall"
                textProps={{
                  variantText: 'body-14-semibold',
                  color: COLOR.darkBlue,
                }}
                customStyle={{
                  height: '40px',
                  borderRadius: '8px',
                  padding: '12px 24px',
                }}
                onClick={handleResetFilter}
              >
                {t('reset')}
              </CommonButton>
              <CommonButton
                variant="contained"
                textProps={{
                  variantText: 'body-14-semibold',
                  color: COLOR.darkBlue,
                }}
                customStyle={{
                  height: '40px',
                  borderRadius: '8px',
                }}
                onClick={handleApplyFilter}
              >
                {t('apply')}
              </CommonButton>
            </div>
          </StyledAdvancedFilter>
        }
        trigger={['click']}
        showArrow={false}
        align={{ offset: [0, 5] }}
        onVisibleChange={handleTooltipVisibleChange}
        visible={isTooltipOpen}
        zIndex={10000}
      >
        <IconButton className="filter-btn">
          <FilterAltRoundedIcon
            sx={{ color: COLOR.navyBlue, fontSize: '24px' }}
          />
          {hasFilter && (
            <FiberManualRecordRoundedIcon
              className="circle-btn"
              sx={{ color: COLOR.textPending, fontSize: '16px !important' }}
            />
          )}
        </IconButton>
      </RcTooltip>
    </>
  );
};

export default AdvancedFilterButton;
